import type {
  HouseComposerView,
  RoomState,
  FloorplanState,
  PanoramaState,
  SelectedItem,
  SelectionType,
} from "../../viewer/HouseComposerView.ts";
import type { HouseFloor, ManifestCapture, ManifestRoom } from "../../types.ts";

import type { MeasurementType, NoteCategory } from "../../types.ts";
import { convertValue } from "../../utils/units.ts";

export interface AnchoredPhoto {
  capture_id: string;
  marker_id: number;
  filename: string;
}

export interface HouseTabOptions {
  container: HTMLElement;
  icProjectId: string;
  composer: HouseComposerView;
  allCaptures: ManifestCapture[];
  roomById: Map<string, ManifestRoom>;
  getAnchoredPhotos: () => AnchoredPhoto[];
  onAddItem: (type: SelectionType, capture: ManifestCapture) => Promise<void>;
  onRemoveItem: (type: SelectionType, index: number) => void;
  onSave: () => Promise<void>;
  onExport: () => Promise<void>;
  onShowPanoramaPreview: (index: number) => Promise<string | null>;
  onGenerateClientLink: () => Promise<{ url: string; expires_in_days: number }>;
  onLoadWalls: (roomIndex: number) => Promise<void>;
  onLoadStreetView: (roomIndex: number) => Promise<void>;
  onResetWalls: (roomIndex: number) => void;
  onSelectFloorplanSource: (roomIndex: number, captureId: string, filename: string, mediaType: "roomplan_json" | "floorplan_svg") => Promise<void>;
  onResetFloorplan: (roomIndex: number) => void;
  onToggleFloorplanMirror: (roomIndex: number) => void;
  onArUcoPhotoClick: (captureId: string, filename: string) => void;
  getThumbUrl: (captureId: string) => Promise<string>;
}

type HouseInnerTab = "scene" | "notes" | "measures" | "anchors";

const SECTION_LABELS: Record<"room" | "panorama", string> = {
  room: "ROOMS",
  panorama: "PANORAMAS",
};

const TYPE_SVG: Record<MeasurementType, string> = {
  distance: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>`,
  area: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>`,
  height: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>`,
  point: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="9"/></svg>`,
};

const TYPE_LABEL: Record<MeasurementType, string> = {
  distance: "Distance",
  area: "Area",
  height: "Height",
  point: "Point",
};

const CATEGORY_SVG: Record<NoteCategory, string> = {
  room: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
  note: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  damage: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
};

function _catLabel(cat: NoteCategory): string {
  return { room: "Room", note: "Note", damage: "Damage" }[cat];
}

function _escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export class HouseTab {
  private opts: HouseTabOptions;
  private listEl!: HTMLElement;
  private capturePickerEl!: HTMLElement;
  private pickerVisible = false;
  private pickerFor: SelectionType | null = null;
  private saveBtn!: HTMLButtonElement;
  private undoBtn!: HTMLButtonElement;
  private clientLinkBtn!: HTMLButtonElement;
  private transformMode: "translate" | "rotate" = "translate";
  private _pendingCaptures: Map<string, { label: string; type: SelectionType }> = new Map();
  private _activePanoIndex: number | null = null;

  private _innerTab: HouseInnerTab = "scene";
  private _tabBtns: Map<HouseInnerTab, HTMLButtonElement> = new Map();
  private _scenePaneEl!: HTMLElement;
  private _notesPaneEl!: HTMLElement;
  private _measuresPaneEl!: HTMLElement;
  private _anchorsPaneEl!: HTMLElement;
  private _floorsBarEl!: HTMLElement;
  private _notesAllVisible = true;
  private _measuresAllVisible = true;
  private _anchorVisibility: Map<number, boolean> = new Map();
  private _anchorExpanded: Set<number> = new Set();

  constructor(opts: HouseTabOptions) {
    this.opts = opts;
    this._injectStyles();
    this.render();

    opts.composer.onSelectionChange = () => this.refreshList();
    opts.composer.onTransformChange = (sel) => this._refreshTransformInputs(sel);
    opts.composer.onUndoStackChange = () => { this.undoBtn.disabled = !opts.composer.canUndo(); };
    opts.composer.onRoomStateChange = (index) => this._refreshRoomRow(index);
    opts.composer.onLoadProgress = (index, pct) => this._updateProgress(index, pct);
    opts.composer.onFloorplanStateChange = (index) => this._refreshFloorplanRow(index);
    opts.composer.onPanoramaStateChange = (index) => this._refreshPanoramaRow(index);
    opts.composer.onActivePanoChange = (index) => {
      this._activePanoIndex = index;
      this._refreshActivePanoHighlight();
    };
    opts.composer.onMeasureChange = () => this._renderMeasures();
    opts.composer.onNoteChange = () => this._renderNotes();
    opts.composer.onFloorChange = () => { this._renderFloorsBar(); this.refreshList(); };
  }

  private _injectStyles(): void {
    if (document.getElementById("house-tab-styles")) return;
    const s = document.createElement("style");
    s.id = "house-tab-styles";
    s.textContent = `
      @keyframes house-progress-slide {
        0%   { left: -45%; width: 45%; }
        100% { left: 100%; width: 45%; }
      }
      .hp-track { position:relative; height:3px; background:var(--border,#333);
                  border-radius:2px; margin-top:5px; overflow:hidden; }
      .hp-fill   { height:100%; background:var(--accent,#5b8dd9);
                   border-radius:2px; transition:width 80ms linear; }
      .hp-indeterminate { position:absolute; top:0; height:100%;
                          background:var(--accent,#5b8dd9);
                          animation:house-progress-slide 1.2s ease-in-out infinite; }
      .house-eye { background:none;border:none;cursor:pointer;padding:0 3px;
                   font-size:13px;line-height:1;color:var(--text-muted);transition:opacity 80ms; }
      .house-eye.hidden { opacity:0.3; }
      .house-preview-btn { background:none;border:none;cursor:pointer;padding:0 3px;
                           font-size:13px;line-height:1;opacity:0.35;transition:opacity 80ms; }
      .house-preview-btn.active { opacity:1; }
    `;
    document.head.appendChild(s);
  }

  // ── Layout ────────────────────────────────────────────────────────────────

  private render(): void {
    const c = this.opts.container;
    c.innerHTML = "";
    c.style.cssText =
      "display:flex;flex-direction:column;height:100%;overflow:hidden;font-size:13px;color:var(--text);";

    // Inner tab bar
    c.appendChild(this._buildInnerTabBar());

    // Scene pane
    this._scenePaneEl = document.createElement("div");
    this._scenePaneEl.style.cssText =
      "flex:1 1 0;min-height:0;overflow:hidden;display:flex;flex-direction:column;";
    this._buildScenePane(this._scenePaneEl);
    c.appendChild(this._scenePaneEl);

    // Notes pane
    this._notesPaneEl = document.createElement("div");
    this._notesPaneEl.style.cssText =
      "flex:1 1 0;min-height:0;overflow:hidden;flex-direction:column;display:none;";
    c.appendChild(this._notesPaneEl);

    // Measures pane
    this._measuresPaneEl = document.createElement("div");
    this._measuresPaneEl.style.cssText =
      "flex:1 1 0;min-height:0;overflow:hidden;flex-direction:column;display:none;";
    c.appendChild(this._measuresPaneEl);

    // Anchors pane
    this._anchorsPaneEl = document.createElement("div");
    this._anchorsPaneEl.style.cssText =
      "flex:1 1 0;min-height:0;overflow-y:auto;flex-direction:column;display:none;";
    c.appendChild(this._anchorsPaneEl);

    this._renderNotes();
    this._renderMeasures();
    this._renderAnchors();
    this._syncTabBar();
  }

  private _buildInnerTabBar(): HTMLElement {
    const bar = document.createElement("div");
    bar.style.cssText =
      "display:flex;border-bottom:1px solid var(--border);background:var(--surface);flex-shrink:0;";
    this._tabBtns.clear();

    const tabs: Array<{ key: HouseInnerTab; label: string }> = [
      { key: "scene", label: "Scene" },
      { key: "notes", label: "Notes" },
      { key: "measures", label: "Measures" },
      { key: "anchors", label: "Anchors" },
    ];

    for (const t of tabs) {
      const btn = document.createElement("button");
      btn.textContent = t.label;
      btn.style.cssText =
        "flex:1;padding:7px 4px;background:none;border:none;" +
        "border-bottom:2px solid transparent;" +
        "color:var(--text-muted);font-size:11px;font-weight:700;" +
        "text-transform:uppercase;letter-spacing:.3px;" +
        "cursor:pointer;transition:color 120ms,border-color 120ms;";
      btn.onclick = () => this._setInnerTab(t.key);
      this._tabBtns.set(t.key, btn);
      bar.appendChild(btn);
    }

    return bar;
  }

  private _setInnerTab(tab: HouseInnerTab): void {
    if (this._innerTab === "anchors" && tab !== "anchors") {
      this._anchorVisibility.clear();
      this.opts.composer.setAllArUcoMarkersVisible(false);
    }
    this._innerTab = tab;
    this._syncTabBar();
  }

  private _syncTabBar(): void {
    for (const [key, btn] of this._tabBtns) {
      const active = key === this._innerTab;
      btn.style.color = active ? "var(--accent)" : "var(--text-muted)";
      btn.style.borderBottomColor = active ? "var(--accent)" : "transparent";
    }
    this._scenePaneEl.style.display = this._innerTab === "scene" ? "flex" : "none";
    this._notesPaneEl.style.display = this._innerTab === "notes" ? "flex" : "none";
    this._measuresPaneEl.style.display = this._innerTab === "measures" ? "flex" : "none";
    this._anchorsPaneEl.style.display = this._innerTab === "anchors" ? "flex" : "none";
  }

  // ── Floors bar ────────────────────────────────────────────────────────────

  private _renderFloorsBar(): void {
    if (!this._floorsBarEl) return;
    this._floorsBarEl.innerHTML = "";
    const floors = this.opts.composer.getFloors();
    if (floors.length === 0) return;

    const activeFloorId = this.opts.composer.getActiveFloorId();
    const bar = document.createElement("div");
    bar.style.cssText =
      "display:flex;align-items:center;gap:4px;padding:5px 8px;" +
      "border-bottom:1px solid var(--border);flex-wrap:wrap;";

    const _floorBtn = (label: string, active: boolean, onClick: () => void): HTMLButtonElement => {
      const btn = document.createElement("button");
      btn.className = "btn";
      btn.textContent = label;
      btn.style.cssText = `font-size:11px;padding:2px 8px;${active ? "border-color:var(--accent);color:var(--accent);" : ""}`;
      btn.onclick = onClick;
      return btn;
    };

    bar.appendChild(_floorBtn("All", activeFloorId == null, () => {
      this.opts.composer.setActiveFloor(null);
    }));

    for (const floor of floors) {
      const isActive = activeFloorId === floor.id;
      const wrap = document.createElement("span");
      wrap.style.cssText = "display:inline-flex;align-items:center;gap:2px;";

      const btn = _floorBtn(floor.label, isActive, () => {
        this.opts.composer.setActiveFloor(isActive ? null : floor.id);
      });

      const editBtn = document.createElement("button");
      editBtn.textContent = "✏";
      editBtn.title = "Rename floor";
      editBtn.style.cssText =
        "background:none;border:none;cursor:pointer;font-size:11px;padding:0 2px;color:var(--text-muted);";
      editBtn.onclick = (e) => {
        e.stopPropagation();
        const name = window.prompt("Floor name:", floor.label);
        if (name?.trim()) {
          const updated = this.opts.composer.getFloors().map(
            (f) => f.id === floor.id ? { ...f, label: name.trim() } : f,
          );
          this.opts.composer.setFloors(updated);
        }
      };

      const delBtn = document.createElement("button");
      delBtn.textContent = "×";
      delBtn.title = "Delete floor";
      delBtn.style.cssText =
        "background:none;border:none;cursor:pointer;font-size:14px;padding:0 2px;" +
        "color:var(--text-muted);line-height:1;";
      delBtn.onclick = (e) => {
        e.stopPropagation();
        if (!window.confirm(`Delete "${floor.label}"? Rooms will become unassigned.`)) return;
        const updated = this.opts.composer.getFloors().filter((f) => f.id !== floor.id);
        this.opts.composer.setFloors(updated);
        if (this.opts.composer.getActiveFloorId() === floor.id) {
          this.opts.composer.setActiveFloor(null);
        }
        this.opts.composer.getRooms().forEach((room, ri) => {
          if (room.entry.floor_id === floor.id) this.opts.composer.setRoomFloorId(ri, null);
        });
      };

      wrap.appendChild(btn);
      wrap.appendChild(editBtn);
      wrap.appendChild(delBtn);
      bar.appendChild(wrap);
    }

    const autoBtn = document.createElement("button");
    autoBtn.className = "btn";
    autoBtn.textContent = "Auto";
    autoBtn.title = "Auto-detect floors from wall geometry";
    autoBtn.style.cssText = "font-size:11px;padding:2px 8px;margin-left:auto;";
    autoBtn.onclick = () => {
      const detected = this.opts.composer.autoDetectFloors();
      if (detected.length === 0) alert("No walls loaded — cannot auto-detect floors.");
    };
    bar.appendChild(autoBtn);

    const addBtn = document.createElement("button");
    addBtn.className = "btn";
    addBtn.textContent = "+ Floor";
    addBtn.title = "Add a floor";
    addBtn.style.cssText = "font-size:11px;padding:2px 8px;";
    addBtn.onclick = () => {
      const existing = this.opts.composer.getFloors();
      const newFloor: HouseFloor = {
        id: crypto.randomUUID(),
        label: `Floor ${existing.length + 1}`,
        index: existing.length,
        y_offset: 0,
      };
      this.opts.composer.setFloors([...existing, newFloor]);
    };
    bar.appendChild(addBtn);

    this._floorsBarEl.appendChild(bar);
  }

  // ── Scene pane ─────────────────────────────────────────────────────────────

  private _buildScenePane(el: HTMLElement): void {
    // Toolbar
    const toolbar = document.createElement("div");
    toolbar.style.cssText =
      "display:flex;align-items:center;gap:6px;padding:8px 10px;border-bottom:1px solid var(--border);flex-shrink:0;";

    const clipBtn = document.createElement("button");
    clipBtn.className = "btn";
    clipBtn.title = "Toggle section cut";
    clipBtn.textContent = "✂ Clip";
    clipBtn.style.cssText = "font-size:12px;padding:4px 10px;";
    toolbar.appendChild(clipBtn);

    const modeBtn = document.createElement("button");
    modeBtn.className = "btn";
    modeBtn.title = "Toggle translate / rotate";
    modeBtn.textContent = "⟳ Rotate";
    modeBtn.style.cssText = "font-size:12px;padding:4px 10px;margin-left:auto;";
    modeBtn.onclick = () => {
      this.transformMode = this.transformMode === "translate" ? "rotate" : "translate";
      modeBtn.textContent = this.transformMode === "translate" ? "⟳ Rotate" : "↔ Move";
      this.opts.composer.setTransformMode(this.transformMode);
    };
    toolbar.appendChild(modeBtn);

    let snapEnabled = false;
    let snapTranslation = 0.05;
    let snapRotation = 1;
    const snapBtn = document.createElement("button");
    snapBtn.className = "btn";
    snapBtn.textContent = "⊞ Snap";
    snapBtn.title = "Toggle grid snap for gizmo dragging";
    snapBtn.style.cssText = "font-size:12px;padding:4px 10px;";
    const updateSnap = () => {
      this.opts.composer.setSnap(snapEnabled, snapTranslation, snapRotation);
      snapBtn.style.borderColor = snapEnabled ? "var(--accent)" : "";
      snapBtn.style.color       = snapEnabled ? "var(--accent)" : "";
      snapRow.style.display     = snapEnabled ? "flex" : "none";
    };
    snapBtn.onclick = () => { snapEnabled = !snapEnabled; updateSnap(); };
    toolbar.appendChild(snapBtn);
    el.appendChild(toolbar);

    // Snap sub-row
    const snapRow = document.createElement("div");
    snapRow.style.cssText =
      "display:none;align-items:center;gap:8px;padding:5px 10px;" +
      "border-bottom:1px solid var(--border);background:var(--surface);flex-shrink:0;flex-wrap:wrap;";

    const _makeSnapField = (label: string, value: number, unit: string, onChange: (v: number) => void): HTMLElement => {
      const wrap = document.createElement("label");
      wrap.style.cssText = "display:flex;align-items:center;gap:4px;font-size:11px;color:var(--text-muted);";
      wrap.textContent = label;
      const inp = document.createElement("input");
      inp.type = "number"; inp.value = String(value); inp.step = "any";
      inp.style.cssText =
        "width:52px;background:var(--surface);border:1px solid var(--border);border-radius:3px;" +
        "color:var(--text);font-size:11px;padding:2px 4px;";
      inp.onchange = () => { const v = parseFloat(inp.value); if (v > 0) { onChange(v); updateSnap(); } };
      wrap.appendChild(inp);
      const u = document.createElement("span"); u.textContent = unit;
      wrap.appendChild(u);
      return wrap;
    };
    snapRow.appendChild(_makeSnapField("Move", snapTranslation, "m", v => { snapTranslation = v; }));
    snapRow.appendChild(_makeSnapField("Rot",  snapRotation,    "°", v => { snapRotation    = v; }));

    const nudgeHint = document.createElement("span");
    nudgeHint.style.cssText =
      "font-size:10px;color:var(--text-muted);margin-left:auto;white-space:nowrap;";
    nudgeHint.textContent = "↑↓←→ / PgUp/Dn · Shift×10 · Ctrl÷10";
    snapRow.appendChild(nudgeHint);
    el.appendChild(snapRow);

    // Section clip sub-row (hidden until Clip is active)
    const clipRow = document.createElement("div");
    clipRow.style.cssText =
      "display:none;align-items:center;gap:8px;padding:5px 10px;" +
      "border-bottom:1px solid var(--border);background:var(--surface);flex-shrink:0;";

    const clipLabel = document.createElement("span");
    clipLabel.style.cssText =
      "font-size:11px;color:var(--text-muted);font-weight:600;white-space:nowrap;";
    clipLabel.textContent = "Section";
    clipRow.appendChild(clipLabel);

    const clipSlider = document.createElement("input");
    clipSlider.type = "range";
    clipSlider.min = "0";
    clipSlider.max = "100";
    clipSlider.value = "85";
    clipSlider.style.cssText = "flex:1;cursor:pointer;accent-color:var(--accent,#5b8dd9);";
    clipRow.appendChild(clipSlider);

    const clipValEl = document.createElement("span");
    clipValEl.style.cssText =
      "font-size:11px;color:var(--text-muted);width:32px;text-align:right;flex-shrink:0;";
    clipValEl.textContent = "85%";
    clipRow.appendChild(clipValEl);

    clipSlider.oninput = () => {
      const pct = parseInt(clipSlider.value);
      clipValEl.textContent = `${pct}%`;
      this.opts.composer.setSectionHeight(pct);
    };

    clipBtn.onclick = () => {
      const nowOn = !this.opts.composer.isClipModeActive;
      this.opts.composer.setClipModeActive(nowOn);
      clipBtn.style.borderColor = nowOn ? "var(--accent)" : "";
      clipBtn.style.color = nowOn ? "var(--accent)" : "";
      clipRow.style.display = nowOn ? "flex" : "none";
      if (nowOn) this.opts.composer.setSectionHeight(parseInt(clipSlider.value));
    };

    el.appendChild(clipRow);

    // Shared picker (shown under the relevant section)
    this.capturePickerEl = document.createElement("div");
    this.capturePickerEl.style.cssText =
      "display:none;flex-direction:column;border-bottom:1px solid var(--border);" +
      "max-height:180px;overflow-y:auto;background:var(--surface);flex-shrink:0;";
    el.appendChild(this.capturePickerEl);

    // Floors filter bar
    this._floorsBarEl = document.createElement("div");
    this._floorsBarEl.style.cssText = "flex-shrink:0;";
    this._renderFloorsBar();
    el.appendChild(this._floorsBarEl);

    // Scrollable sections area
    this.listEl = document.createElement("div");
    this.listEl.style.cssText = "flex:1 1 0;overflow-y:auto;";
    el.appendChild(this.listEl);

    // Client link row (above the save/export footer)
    const clientLinkRow = document.createElement("div");
    clientLinkRow.style.cssText =
      "padding:6px 10px;border-top:1px solid var(--border);flex-shrink:0;";

    this.clientLinkBtn = document.createElement("button");
    this.clientLinkBtn.className = "btn";
    this.clientLinkBtn.textContent = "Generate client link";
    this.clientLinkBtn.style.cssText = "width:100%;font-size:12px;padding:5px;";
    this.clientLinkBtn.onclick = () => this._doGenerateClientLink();
    clientLinkRow.appendChild(this.clientLinkBtn);
    el.appendChild(clientLinkRow);

    // Footer
    const footer = document.createElement("div");
    footer.style.cssText =
      "display:flex;gap:6px;padding:8px 10px;border-top:1px solid var(--border);flex-shrink:0;";

    this.undoBtn = document.createElement("button");
    this.undoBtn.className = "btn";
    this.undoBtn.textContent = "Undo";
    this.undoBtn.style.cssText = "flex:1;font-size:12px;padding:5px;";
    this.undoBtn.disabled = !this.opts.composer.canUndo();
    this.undoBtn.onclick = () => { this.opts.composer.undo(); this.refreshList(); };
    footer.appendChild(this.undoBtn);

    this.saveBtn = document.createElement("button");
    this.saveBtn.className = "btn";
    this.saveBtn.textContent = "Save";
    this.saveBtn.style.cssText = "flex:1;font-size:12px;padding:5px;";
    this.saveBtn.onclick = () => this._doSave();
    footer.appendChild(this.saveBtn);

    const exportBtn = document.createElement("button");
    exportBtn.className = "btn";
    exportBtn.textContent = "Export GLB";
    exportBtn.style.cssText = "flex:1;font-size:12px;padding:5px;";
    exportBtn.onclick = () => this._doExport(exportBtn);
    footer.appendChild(exportBtn);

    el.appendChild(footer);

    this.refreshList();
  }

  // ── Notes pane ─────────────────────────────────────────────────────────────

  private _buildVisibilityToolbar(
    count: number,
    noun: string,
    allVisible: boolean,
    onToggle: (visible: boolean) => void,
  ): HTMLElement {
    const bar = document.createElement("div");
    bar.style.cssText =
      "display:flex;align-items:center;padding:5px 10px;" +
      "border-bottom:1px solid var(--border);background:var(--surface-2,#151720);flex-shrink:0;";

    const btn = document.createElement("button");
    btn.style.cssText =
      "background:none;border:none;cursor:pointer;padding:0;display:flex;align-items:center;" +
      "gap:5px;color:var(--text-muted);font-size:11px;font-weight:600;transition:color 80ms;";
    btn.onmouseenter = () => { btn.style.color = "var(--text)"; };
    btn.onmouseleave = () => { btn.style.color = "var(--text-muted)"; };

    const eye = document.createElement("span");
    eye.style.cssText = `font-size:13px;opacity:${allVisible ? "1" : "0.35"};`;
    eye.textContent = "👁";
    btn.appendChild(eye);

    const lbl = document.createElement("span");
    lbl.textContent = allVisible ? "Hide all" : "Show all";
    btn.appendChild(lbl);

    btn.onclick = () => onToggle(!allVisible);
    bar.appendChild(btn);

    const counter = document.createElement("span");
    counter.style.cssText =
      "margin-left:auto;font-size:10px;color:var(--text-muted);";
    counter.textContent = `${count} ${noun}${count !== 1 ? "s" : ""}`;
    bar.appendChild(counter);

    return bar;
  }

  private _renderNotes(): void {
    if (!this._notesPaneEl) return;
    this._notesPaneEl.innerHTML = "";
    const notes = this.opts.composer.getNotes();

    if (notes.length === 0) {
      const empty = document.createElement("div");
      empty.className = "np-empty";
      empty.innerHTML = "No notes yet.<br>Use the Notes tool in the viewer.";
      this._notesPaneEl.appendChild(empty);
      return;
    }

    this._notesPaneEl.appendChild(this._buildVisibilityToolbar(
      notes.length,
      "note",
      this._notesAllVisible,
      (visible) => {
        this._notesAllVisible = visible;
        this.opts.composer.setAllNotesVisible(visible);
        this._renderNotes();
      },
    ));

    const list = document.createElement("div");
    list.className = "np-list";
    list.style.cssText = "flex:1 1 0;overflow-y:auto;";
    this._notesPaneEl.appendChild(list);

    for (const n of notes) {
      const item = document.createElement("div");
      item.className = "np-item";
      item.dataset.id = n.id;
      item.innerHTML = `
        <span class="np-icon np-icon--${n.category}">${CATEGORY_SVG[n.category]}</span>
        <div class="np-info">
          <span class="np-cat">${_catLabel(n.category)}</span>
          <span class="np-text">${_escHtml(n.text)}</span>
        </div>
        <button class="np-del" title="Delete" data-id="${n.id}">×</button>
      `;
      item.addEventListener("click", () => this.opts.composer.highlightNote(n.id));
      item.querySelector<HTMLButtonElement>(".np-del")!.addEventListener("click", (e) => {
        e.stopPropagation();
        this.opts.composer.removeNote((e.currentTarget as HTMLElement).dataset.id!);
      });
      list.appendChild(item);
    }
  }

  // ── Measures pane ──────────────────────────────────────────────────────────

  private _renderMeasures(): void {
    if (!this._measuresPaneEl) return;
    this._measuresPaneEl.innerHTML = "";
    const measurements = this.opts.composer.getMeasurements();

    if (measurements.length === 0) {
      const empty = document.createElement("div");
      empty.className = "mp-empty";
      empty.innerHTML = "No measurements yet.<br>Use the Measure tool in the viewer.";
      this._measuresPaneEl.appendChild(empty);
      return;
    }

    this._measuresPaneEl.appendChild(this._buildVisibilityToolbar(
      measurements.length,
      "measurement",
      this._measuresAllVisible,
      (visible) => {
        this._measuresAllVisible = visible;
        this.opts.composer.setAllMeasurementsVisible(visible);
        this._renderMeasures();
      },
    ));

    const list = document.createElement("div");
    list.className = "mp-list";
    list.style.cssText = "flex:1 1 0;overflow-y:auto;";
    this._measuresPaneEl.appendChild(list);

    for (const m of measurements) {
      const item = document.createElement("div");
      item.className = "mp-item";
      item.dataset.id = m.id;
      const cv = convertValue(m.value, m.type);
      const val = `${cv.value.toFixed(2)} ${cv.unit}`;
      item.innerHTML = `
        <span class="mp-icon mp-icon--${m.type}">${TYPE_SVG[m.type]}</span>
        <div class="mp-info">
          <span class="mp-type">${TYPE_LABEL[m.type]}</span>
          <span class="mp-value">${val}</span>
        </div>
        <button class="mp-del" title="Delete" data-id="${m.id}">×</button>
      `;
      item.addEventListener("click", () => this.opts.composer.highlightMeasurement(m.id));
      item.querySelector<HTMLButtonElement>(".mp-del")!.addEventListener("click", (e) => {
        e.stopPropagation();
        this.opts.composer.removeMeasurement((e.currentTarget as HTMLElement).dataset.id!);
      });
      list.appendChild(item);
    }
  }

  // ── Anchors pane ──────────────────────────────────────────────────────────

  renderAnchors(): void { this._renderAnchors(); }

  private _renderAnchors(): void {
    if (!this._anchorsPaneEl) return;
    this._anchorsPaneEl.innerHTML = "";

    const markers = this.opts.composer.getArUcoMarkers();

    if (markers.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText = "padding:24px 16px;color:var(--text-muted);font-size:13px;text-align:center;line-height:1.6;";
      empty.textContent = "No ArUco anchors loaded.";
      this._anchorsPaneEl.appendChild(empty);
      return;
    }

    const sorted = [...markers].sort((a, b) => a.marker_id - b.marker_id);
    const allVisible = sorted.length > 0 && sorted.every(m => this._anchorVisibility.get(m.marker_id) === true);

    this._anchorsPaneEl.appendChild(this._buildVisibilityToolbar(
      sorted.length,
      "anchor",
      allVisible,
      (visible) => {
        for (const { marker_id } of sorted) {
          this._anchorVisibility.set(marker_id, visible);
          this.opts.composer.setArUcoMarkerVisible(marker_id, visible);
        }
        this._renderAnchors();
      },
    ));

    // Group anchored photos by marker_id
    const photosByMarker = new Map<number, AnchoredPhoto[]>();
    for (const p of this.opts.getAnchoredPhotos()) {
      const arr = photosByMarker.get(p.marker_id) ?? [];
      arr.push(p);
      photosByMarker.set(p.marker_id, arr);
    }

    const list = document.createElement("div");
    list.style.cssText = "display:flex;flex-direction:column;gap:4px;padding:8px;flex:1 1 0;overflow-y:auto;";
    this._anchorsPaneEl.appendChild(list);

    const fmt = (v: number) => v.toFixed(2);
    for (const { marker_id, worldPos } of sorted) {
      const visible = this._anchorVisibility.get(marker_id) === true;
      const photos = photosByMarker.get(marker_id) ?? [];
      const expanded = this._anchorExpanded.has(marker_id);

      const card = document.createElement("div");
      card.style.cssText =
        "background:var(--surface2,#222);border-radius:6px;border:1px solid var(--border);overflow:hidden;";

      // ── Header row ──────────────────────────────────────────────────────
      const header = document.createElement("div");
      header.style.cssText =
        "display:flex;align-items:center;gap:8px;padding:8px 10px;cursor:pointer;transition:border-color 120ms;";
      header.onmouseenter = () => { header.style.background = "var(--hover,rgba(255,255,255,.04))"; };
      header.onmouseleave = () => { header.style.background = ""; };
      header.addEventListener("click", () => this.opts.composer.focusOnArUcoMarker(marker_id));

      const idSpan = document.createElement("span");
      idSpan.style.cssText = "font-size:12px;font-weight:700;color:var(--accent);flex-shrink:0;min-width:28px;";
      idSpan.textContent = `#${marker_id}`;

      const coords = document.createElement("span");
      coords.style.cssText = "font-size:11px;color:var(--text-muted);font-family:monospace;flex:1;";
      coords.textContent = `x ${fmt(worldPos.x)}  y ${fmt(worldPos.y)}  z ${fmt(worldPos.z)}`;

      const eyeBtn = document.createElement("button");
      eyeBtn.className = "house-eye" + (visible ? "" : " hidden");
      eyeBtn.textContent = "👁";
      eyeBtn.title = visible ? "Hide anchor" : "Show anchor";
      eyeBtn.onclick = (e) => {
        e.stopPropagation();
        const next = !this._anchorVisibility.get(marker_id);
        this._anchorVisibility.set(marker_id, next);
        this.opts.composer.setArUcoMarkerVisible(marker_id, next);
        this._renderAnchors();
      };

      header.appendChild(idSpan);
      header.appendChild(coords);
      header.appendChild(eyeBtn);
      card.appendChild(header);

      // ── Photos section (only when marker has photos) ─────────────────────
      if (photos.length > 0) {
        const photoToggle = document.createElement("div");
        photoToggle.style.cssText =
          "display:flex;align-items:center;gap:6px;padding:4px 10px 6px;" +
          "border-top:1px solid var(--border);cursor:pointer;user-select:none;";
        photoToggle.onmouseenter = () => { photoToggle.style.background = "var(--hover,rgba(255,255,255,.04))"; };
        photoToggle.onmouseleave = () => { photoToggle.style.background = ""; };

        const arrow = document.createElement("span");
        arrow.style.cssText = "font-size:10px;color:var(--text-muted);transition:transform 120ms;";
        arrow.textContent = "▶";
        if (expanded) arrow.style.transform = "rotate(90deg)";

        const photoLabel = document.createElement("span");
        photoLabel.style.cssText = "font-size:11px;color:var(--text-muted);";
        photoLabel.textContent = `Photos (${photos.length})`;

        photoToggle.appendChild(arrow);
        photoToggle.appendChild(photoLabel);
        card.appendChild(photoToggle);

        // Photo strip (shown when expanded)
        const strip = document.createElement("div");
        strip.style.cssText =
          "display:flex;flex-wrap:wrap;gap:4px;padding:6px 10px 8px;" +
          `border-top:1px solid var(--border);${expanded ? "" : "display:none;"}`;
        strip.style.display = expanded ? "flex" : "none";

        photoToggle.addEventListener("click", (e) => {
          e.stopPropagation();
          if (this._anchorExpanded.has(marker_id)) {
            this._anchorExpanded.delete(marker_id);
            strip.style.display = "none";
            arrow.style.transform = "";
          } else {
            this._anchorExpanded.add(marker_id);
            strip.style.display = "flex";
            arrow.style.transform = "rotate(90deg)";
          }
        });

        for (const photo of photos) {
          const thumb = document.createElement("img");
          thumb.style.cssText =
            "width:56px;height:56px;object-fit:cover;border-radius:4px;" +
            "cursor:pointer;border:1px solid var(--border);flex-shrink:0;background:var(--surface,#1a1c24);";
          thumb.title = photo.filename;
          this.opts.getThumbUrl(photo.capture_id).then((url) => { thumb.src = url; }).catch(() => {});
          thumb.addEventListener("click", (e) => {
            e.stopPropagation();
            this.opts.onArUcoPhotoClick(photo.capture_id, photo.filename);
          });
          strip.appendChild(thumb);
        }

        card.appendChild(strip);
      }

      list.appendChild(card);
    }
  }

  // ── Picker ────────────────────────────────────────────────────────────────

  private _togglePicker(type: SelectionType): void {
    if (this.pickerVisible && this.pickerFor === type) {
      this.pickerVisible = false;
      this.capturePickerEl.style.display = "none";
      return;
    }
    this.pickerFor = type;
    this.pickerVisible = true;
    this._buildPicker();
    this.capturePickerEl.style.display = "flex";
  }

  private _buildPicker(): void {
    const type = this.pickerFor!;
    this.capturePickerEl.innerHTML = "";

    const header = document.createElement("div");
    header.style.cssText =
      "padding:6px 10px;font-size:11px;font-weight:700;text-transform:uppercase;" +
      "letter-spacing:.4px;color:var(--text-muted);";
    header.textContent = `Add ${type}`;
    this.capturePickerEl.appendChild(header);

    const loadedIds = new Set(
      type === "room"
        ? this.opts.composer.getRooms().map((r) => r.entry.capture_id)
        : type === "panorama"
          ? this.opts.composer.getPanoramas().map((p) => p.entry.capture_id)
          : [],
    );
    const pendingIds = new Set(
      [...this._pendingCaptures.entries()]
        .filter(([, v]) => v.type === type)
        .map(([id]) => id),
    );

    const candidates = this.opts.allCaptures.filter((c) => {
      if (loadedIds.has(c.id) || pendingIds.has(c.id)) return false;
      if (type === "room") return c.media_type === "glb";
      if (type === "panorama") return c.media_type === "panorama_frame";
      return false;
    });

    if (candidates.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText = "padding:10px;color:var(--text-muted);font-size:12px;";
      empty.textContent = "No captures available.";
      this.capturePickerEl.appendChild(empty);
      return;
    }

    for (const cap of candidates) {
      const room = cap.room_id ? this.opts.roomById.get(cap.room_id) : null;
      const label = room?.name ?? cap.filename ?? cap.id;

      const row = document.createElement("div");
      row.style.cssText =
        "padding:7px 12px;cursor:pointer;border-bottom:1px solid var(--border);";
      row.onmouseenter = () => (row.style.background = "var(--hover)");
      row.onmouseleave = () => (row.style.background = "");

      const name = document.createElement("div");
      name.style.cssText = "font-size:12px;font-weight:600;";
      name.textContent = label;
      row.appendChild(name);

      const sub = document.createElement("div");
      sub.style.cssText = "font-size:11px;color:var(--text-muted);margin-top:2px;";
      sub.textContent = cap.filename ?? cap.id;
      row.appendChild(sub);

      row.onclick = async () => {
        this._pendingCaptures.set(cap.id, { label, type });
        this._togglePicker(type);
        this.refreshList();
        try {
          await this.opts.onAddItem(type, cap);
        } finally {
          this._pendingCaptures.delete(cap.id);
          this.refreshList();
        }
      };
      this.capturePickerEl.appendChild(row);
    }
  }

  // ── Sections ──────────────────────────────────────────────────────────────

  refreshList(): void {
    this.listEl.innerHTML = "";
    this.listEl.appendChild(this._buildSection("room"));
    this.listEl.appendChild(this._buildSection("panorama"));
  }

  private _buildSection(type: "room" | "panorama"): HTMLElement {
    const section = document.createElement("div");
    section.setAttribute("data-section", type);

    // Section header
    const header = document.createElement("div");
    header.style.cssText =
      "display:flex;align-items:center;padding:5px 10px 5px 8px;" +
      "border-bottom:1px solid var(--border);background:var(--surface-2,#151720);";

    const title = document.createElement("span");
    title.style.cssText =
      "font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;" +
      "color:var(--text-muted);flex:1;";
    title.textContent = SECTION_LABELS[type];
    header.appendChild(title);

    if (type === "panorama") {
      let markersOn = false;
      this.opts.composer.setPanoramaMarkersVisible(false);

      const eyeBtn = document.createElement("button");
      eyeBtn.className = "house-eye hidden";
      eyeBtn.title = "Show/hide panorama markers";
      eyeBtn.style.cssText = "margin-right:4px;";
      eyeBtn.textContent = "👁";
      eyeBtn.onclick = () => {
        markersOn = !markersOn;
        this.opts.composer.setPanoramaMarkersVisible(markersOn);
        eyeBtn.className = "house-eye" + (markersOn ? "" : " hidden");
      };
      header.appendChild(eyeBtn);
    }

    const addBtn = document.createElement("button");
    addBtn.className = "btn";
    addBtn.textContent = "+ Add";
    addBtn.style.cssText = "font-size:11px;padding:2px 8px;";
    addBtn.onclick = () => this._togglePicker(type);
    header.appendChild(addBtn);
    section.appendChild(header);

    // Items
    if (type === "room") {
      const rooms = this.opts.composer.getRooms();
      const floors = this.opts.composer.getFloors();
      const activeFloorId = this.opts.composer.getActiveFloorId();
      const loadedIds = new Set(rooms.map((r) => r.entry.capture_id));

      if (floors.length === 0) {
        // No floors defined — flat list
        rooms.forEach((room, i) => section.appendChild(this._buildRoomRow(room, i)));
      } else {
        // Grouped by floor
        const sortedFloors = [...floors].sort((a, b) => a.index - b.index);
        for (const floor of sortedFloors) {
          if (activeFloorId !== null && activeFloorId !== floor.id) continue;
          const floorRooms = rooms.map((r, i) => ({ room: r, i }))
            .filter(({ room }) => room.entry.floor_id === floor.id);
          section.appendChild(this._buildFloorGroupHeader(floor, floorRooms.length));
          floorRooms.forEach(({ room, i }) => section.appendChild(this._buildRoomRow(room, i)));
        }
        // Unassigned rooms
        if (activeFloorId === null) {
          const knownIds = new Set(floors.map((f) => f.id));
          const unassigned = rooms.map((r, i) => ({ room: r, i }))
            .filter(({ room }) => !room.entry.floor_id || !knownIds.has(room.entry.floor_id));
          if (unassigned.length > 0) {
            section.appendChild(this._buildFloorGroupHeader(null, unassigned.length));
            unassigned.forEach(({ room, i }) => section.appendChild(this._buildRoomRow(room, i)));
          }
        }
      }

      for (const [id, v] of this._pendingCaptures) {
        if (v.type === "room" && !loadedIds.has(id))
          section.appendChild(this._buildPendingRow(id, v.label));
      }

      if (rooms.length === 0 && !this._hasPending("room")) {
        section.appendChild(this._emptyMsg("No rooms added yet."));
      }
    } else {
      const panos = this.opts.composer.getPanoramas();
      const loadedIds = new Set(panos.map((p) => p.entry.capture_id));
      panos.forEach((pano, i) => section.appendChild(this._buildPanoramaRow(pano, i)));

      for (const [id, v] of this._pendingCaptures) {
        if (v.type === "panorama" && !loadedIds.has(id))
          section.appendChild(this._buildPendingRow(id, v.label));
      }

      if (panos.length === 0 && !this._hasPending("panorama")) {
        section.appendChild(this._emptyMsg("No panoramas added yet."));
      }
    }

    return section;
  }

  private _hasPending(type: SelectionType): boolean {
    return [...this._pendingCaptures.values()].some((v) => v.type === type);
  }

  private _buildRoomStreetViewSubRow(room: RoomState, index: number): HTMLElement {
    const sub = document.createElement("div");
    sub.setAttribute("data-sv-room-index", String(index));
    sub.style.cssText =
      "margin:0 0 2px 12px;padding:4px 8px;border-left:2px solid var(--border);" +
      "border-radius:0 4px 4px 0;display:flex;align-items:center;gap:5px;";

    if (room.svLoading) {
      sub.style.cssText += "display:flex;align-items:center;gap:6px;";
      const spin = document.createElement("span");
      spin.style.cssText = "font-size:10px;color:var(--text-muted);";
      spin.textContent = "Loading streetview…";
      sub.appendChild(spin);
      return sub;
    }

    if (room.svError) {
      sub.style.cssText += "display:flex;align-items:center;gap:6px;";
      const err = document.createElement("span");
      err.style.cssText = "font-size:10px;color:var(--red);flex:1;";
      err.textContent = `Streetview error: ${room.svError}`;
      sub.appendChild(err);
      const reloadBtn = document.createElement("button");
      reloadBtn.style.cssText = "background:none;border:none;cursor:pointer;padding:0 2px;line-height:1;display:flex;align-items:center;color:var(--text-muted);font-size:14px;";
      reloadBtn.title = "Retry";
      reloadBtn.textContent = "↺";
      reloadBtn.onclick = async (e) => { e.stopPropagation(); await this.opts.onLoadStreetView(index); };
      sub.appendChild(reloadBtn);
      return sub;
    }

    const icon = document.createElement("span");
    icon.style.cssText = "font-size:11px;color:#4a9eff;flex-shrink:0;";
    icon.textContent = "●";
    sub.appendChild(icon);

    const label = document.createElement("span");
    label.style.cssText =
      "flex:1;font-size:11px;color:var(--text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";
    label.textContent = "Streetview";
    sub.appendChild(label);

    if (room.svMarkers) {
      const nodeCount = document.createElement("span");
      nodeCount.style.cssText = "font-size:10px;color:var(--text-muted);flex-shrink:0;";
      nodeCount.textContent = `${room.svMarkers.sortedNodeIds.length}`;
      sub.appendChild(nodeCount);
    }

    const reloadBtn = document.createElement("button");
    reloadBtn.style.cssText = "background:none;border:none;cursor:pointer;padding:0 2px;line-height:1;display:flex;align-items:center;color:var(--text-muted);font-size:14px;";
    reloadBtn.title = "Reload streetview";
    reloadBtn.textContent = "↺";
    reloadBtn.onclick = async (e) => {
      e.stopPropagation();
      await this.opts.onLoadStreetView(index);
    };
    sub.appendChild(reloadBtn);

    const visible = room.entry.streetview_visible ?? false;
    const eyeBtn = document.createElement("button");
    eyeBtn.className = "house-eye" + (visible ? "" : " hidden");
    eyeBtn.title = "Show/hide streetview markers";
    eyeBtn.textContent = "👁";
    eyeBtn.onclick = (e) => {
      e.stopPropagation();
      const v = !(room.entry.streetview_visible ?? false);
      this.opts.composer.setRoomStreetViewVisible(index, v);
      eyeBtn.className = "house-eye" + (v ? "" : " hidden");
      sub.style.opacity = v ? "1" : "0.5";
    };
    sub.appendChild(eyeBtn);

    sub.style.opacity = (room.entry.streetview_visible ?? false) ? "1" : "0.5";
    return sub;
  }

  private _emptyMsg(text: string): HTMLElement {
    const el = document.createElement("div");
    el.style.cssText =
      "padding:8px 12px;color:var(--text-muted);font-size:11px;font-style:italic;";
    el.textContent = text;
    return el;
  }

  // ── Row builders ──────────────────────────────────────────────────────────

  private _buildFloorGroupHeader(floor: HouseFloor | null, count: number): HTMLElement {
    const el = document.createElement("div");
    el.style.cssText =
      "display:flex;align-items:center;padding:4px 10px;" +
      "background:var(--surface-2,#151720);border-bottom:1px solid var(--border);" +
      "border-top:1px solid var(--border);";

    const lbl = document.createElement("span");
    lbl.style.cssText =
      "font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;" +
      "color:var(--accent);flex:1;";
    lbl.textContent = floor ? floor.label : "Unassigned";
    el.appendChild(lbl);

    const cnt = document.createElement("span");
    cnt.style.cssText = "font-size:10px;color:var(--text-muted);";
    cnt.textContent = String(count);
    el.appendChild(cnt);

    return el;
  }

  private _buildRoomRow(room: RoomState, index: number): HTMLElement {
    const sel = this.opts.composer.getSelected();
    const isSelected = sel?.type === "room" && sel.index === index;

    const row = document.createElement("div");
    row.setAttribute("data-room-index", String(index));
    row.style.cssText = `
      padding:7px 10px;border-bottom:1px solid var(--border);cursor:pointer;
      background:${isSelected ? "var(--hover)" : ""};
      border-left:2px solid ${isSelected ? "var(--accent)" : "transparent"};
      transition:background 80ms;
    `;
    row.onclick = () => {
      this.opts.composer.selectItem({ type: "room", index });
      this.refreshList();
    };

    row.appendChild(this._buildRowHead(
      room.entry.label,
      room.entry.visible,
      room.loading,
      room.error,
      (visible) => { this.opts.composer.setRoomVisible(index, visible); row.style.opacity = visible ? "1" : "0.5"; },
      () => { this.opts.onRemoveItem("room", index); this.refreshList(); },
    ));

    // Floor badge — only when floors are defined
    const floors = this.opts.composer.getFloors();
    if (floors.length > 0) {
      const badge = document.createElement("button");
      const currentFloor = floors.find((f) => f.id === room.entry.floor_id);
      badge.textContent = currentFloor ? currentFloor.label : "—";
      badge.title = "Click to assign to next floor";
      badge.style.cssText =
        "font-size:10px;padding:1px 6px;margin-top:2px;border-radius:3px;" +
        "background:var(--surface-2,#2a2a2a);border:1px solid var(--border);" +
        "cursor:pointer;color:var(--text-muted);display:block;text-align:left;";
      badge.onclick = (e) => {
        e.stopPropagation();
        const options = [null, ...floors.map((f) => f.id)];
        const cur = options.indexOf(room.entry.floor_id ?? null);
        const next = options[(cur + 1) % options.length];
        this.opts.composer.setRoomFloorId(index, next);
      };
      row.appendChild(badge);
    }

    if (room.loading) row.appendChild(this._buildProgressBar(index, room.progress));

    if (room.error) {
      const err = document.createElement("div");
      err.style.cssText = "font-size:11px;color:var(--red);margin-top:3px;";
      err.textContent = room.error;
      row.appendChild(err);
    }

    if (isSelected && !room.loading && !room.error) {
      row.appendChild(this._buildRoomInputs(index));
    }

    if (room.entry.walls_capture_id) {
      row.appendChild(this._buildWallsSubRow(room, index));
    }

    // Streetview sub-row: show if loaded, loading, or errored (but not 404 = never found)
    if (room.svMarkers !== null || room.svLoading || (room.svError !== null)) {
      row.appendChild(this._buildRoomStreetViewSubRow(room, index));
    }

    const roomId = this.opts.allCaptures.find((c) => c.id === room.entry.capture_id)?.room_id;
    const fpSources = this.opts.allCaptures.filter(
      (c) => c.room_id === roomId && c.uploaded &&
             (c.media_type === "roomplan_json" || c.media_type === "floorplan_svg") &&
             !c.filename?.includes("_summary"),
    );
    if (fpSources.length > 0) {
      row.appendChild(this._buildFloorplanSourceRow(room, index, fpSources));
    }

    row.style.opacity = room.entry.visible ? "1" : "0.5";
    return row;
  }

  private _buildWallsSubRow(room: RoomState, index: number): HTMLElement {
    const sel = this.opts.composer.getSelected();
    const isWallsSelected = sel?.type === "walls" && sel.index === index;

    const sub = document.createElement("div");
    sub.style.cssText =
      "margin:0 0 2px 12px;padding:4px 8px;border-left:2px solid var(--border);" +
      `background:${isWallsSelected ? "rgba(74,158,255,.08)" : ""};` +
      "border-radius:0 4px 4px 0;";

    if (room.wallsLoading) {
      sub.style.cssText += "display:flex;align-items:center;gap:6px;";
      const icon = document.createElement("span");
      icon.style.cssText = "font-size:12px;color:#4a9eff;";
      icon.textContent = "▦";
      sub.appendChild(icon);
      const lbl = document.createElement("span");
      lbl.style.cssText = "font-size:11px;color:var(--text-muted);";
      lbl.textContent = "Walls loading…";
      sub.appendChild(lbl);
      return sub;
    }

    if (room.wallsError) {
      sub.style.cssText += "display:flex;align-items:center;gap:6px;";
      const lbl = document.createElement("span");
      lbl.style.cssText = "font-size:11px;color:var(--red);";
      lbl.textContent = `Walls error: ${room.wallsError}`;
      sub.appendChild(lbl);
      return sub;
    }

    if (!room.wallsGroup) {
      // Not yet loaded — show Load button
      sub.style.cssText += "display:flex;align-items:center;gap:6px;";
      const icon = document.createElement("span");
      icon.style.cssText = "font-size:12px;color:var(--text-muted);";
      icon.textContent = "▦";
      sub.appendChild(icon);
      const lbl = document.createElement("span");
      lbl.style.cssText = "font-size:11px;color:var(--text-muted);flex:1;";
      lbl.textContent = "Walls";
      sub.appendChild(lbl);
      const resetBtn = document.createElement("button");
      resetBtn.style.cssText = "background:none;border:none;cursor:pointer;padding:0 2px;line-height:1;display:flex;align-items:center;color:var(--text-muted);font-size:14px;";
      resetBtn.title = "Reset walls position";
      resetBtn.textContent = "↺";
      resetBtn.onclick = async (e) => {
        e.stopPropagation();
        this.opts.onResetWalls(index);
        await this.opts.onSave();
      };
      sub.appendChild(resetBtn);
      const loadBtn = document.createElement("button");
      loadBtn.className = "btn";
      loadBtn.textContent = "Load";
      loadBtn.style.cssText = "font-size:10px;padding:2px 7px;";
      loadBtn.onclick = async (e) => {
        e.stopPropagation();
        loadBtn.disabled = true;
        loadBtn.textContent = "…";
        try {
          await this.opts.onLoadWalls(index);
        } finally {
          this.refreshList();
        }
      };
      sub.appendChild(loadBtn);
      return sub;
    }

    // Loaded state — clickable row with eye toggle
    sub.style.cssText += "display:flex;flex-direction:column;gap:3px;cursor:pointer;";
    sub.onclick = (e) => {
      e.stopPropagation();
      this.opts.composer.selectItem({ type: "walls", index });
      this.refreshList();
    };

    const head = document.createElement("div");
    head.style.cssText = "display:flex;align-items:center;gap:6px;";

    const icon = document.createElement("span");
    icon.style.cssText = `font-size:12px;color:${isWallsSelected ? "#4a9eff" : "var(--text-muted)"};`;
    icon.textContent = "▦";
    head.appendChild(icon);

    const lbl = document.createElement("span");
    lbl.style.cssText =
      `font-size:11px;flex:1;color:${isWallsSelected ? "var(--text)" : "var(--text-muted)"};font-weight:${isWallsSelected ? "600" : "400"};`;
    lbl.textContent = "Walls";
    head.appendChild(lbl);

    const resetBtn = document.createElement("button");
    resetBtn.style.cssText = "background:none;border:none;cursor:pointer;padding:0 2px;line-height:1;display:flex;align-items:center;color:var(--text-muted);font-size:14px;";
    resetBtn.title = "Reset walls position";
    resetBtn.textContent = "↺";
    resetBtn.onclick = async (e) => {
      e.stopPropagation();
      this.opts.onResetWalls(index);
      await this.opts.onSave();
      this.refreshList();
    };
    head.appendChild(resetBtn);

    const wallsVisible = room.entry.walls_visible !== false;
    const eyeBtn = document.createElement("button");
    eyeBtn.className = "house-eye" + (wallsVisible ? "" : " hidden");
    eyeBtn.textContent = "👁";
    eyeBtn.title = wallsVisible ? "Hide walls" : "Show walls";
    eyeBtn.onclick = (e) => {
      e.stopPropagation();
      this.opts.composer.setRoomWallsVisible(index, !wallsVisible);
      this.refreshList();
    };
    head.appendChild(eyeBtn);
    sub.appendChild(head);

    // Opacity slider — only when walls are selected
    if (isWallsSelected) {
      const sliderRow = document.createElement("div");
      sliderRow.style.cssText = "display:flex;align-items:center;gap:6px;padding:2px 0 2px 18px;";

      const slider = document.createElement("input");
      slider.type = "range";
      slider.min = "0";
      slider.max = "100";
      const currentOpacity = room.entry.walls_opacity ?? 0.35;
      slider.value = String(Math.round(currentOpacity * 100));
      slider.style.cssText = "flex:1;cursor:pointer;accent-color:#4a9eff;";

      const valEl = document.createElement("span");
      valEl.style.cssText = "font-size:11px;color:var(--text-muted);width:32px;text-align:right;flex-shrink:0;";
      valEl.textContent = `${slider.value}%`;

      slider.oninput = () => {
        const pct = parseInt(slider.value);
        valEl.textContent = `${pct}%`;
        this.opts.composer.setRoomWallsOpacity(index, pct / 100);
      };
      slider.onclick = (e) => e.stopPropagation();

      sliderRow.appendChild(slider);
      sliderRow.appendChild(valEl);
      sub.appendChild(sliderRow);
    }

    return sub;
  }

  private _buildFloorplanSourceRow(room: RoomState, index: number, sources: ManifestCapture[]): HTMLElement {
    const sel = this.opts.composer.getSelected();
    const isSelected = sel?.type === "roomFloorplan" && sel.index === index;

    const sub = document.createElement("div");
    sub.style.cssText =
      "margin:0 0 2px 12px;padding:4px 8px;border-left:2px solid var(--border);" +
      `background:${isSelected ? "rgba(74,158,255,.08)" : ""};` +
      "border-radius:0 4px 4px 0;";

    if (room.floorplanLoading) {
      sub.style.cssText += "display:flex;align-items:center;gap:6px;";
      const spinIcon = document.createElement("span");
      spinIcon.style.cssText = "font-size:12px;color:#4a9eff;";
      spinIcon.textContent = "⬛";
      sub.appendChild(spinIcon);
      const spinLbl = document.createElement("span");
      spinLbl.style.cssText = "font-size:11px;color:var(--text-muted);";
      spinLbl.textContent = "Floor plan loading…";
      sub.appendChild(spinLbl);
      return sub;
    }

    if (room.floorplanError) {
      sub.style.cssText += "display:flex;align-items:center;gap:6px;";
      const errLbl = document.createElement("span");
      errLbl.style.cssText = "font-size:11px;color:var(--red);";
      errLbl.textContent = `Floor plan error: ${room.floorplanError}`;
      sub.appendChild(errLbl);
      return sub;
    }

    sub.style.cssText += "display:flex;flex-direction:column;gap:3px;cursor:pointer;";
    sub.onclick = async (e) => {
      e.stopPropagation();
      this.opts.composer.selectItem({ type: "roomFloorplan", index });
      this.refreshList();
      // Auto-load if a source is already selected but not yet loaded.
      if (!room.floorplanGroup && room.entry.floorplan_capture_id && room.entry.floorplan_filename && room.entry.floorplan_media_type) {
        await this.opts.onSelectFloorplanSource(
          index,
          room.entry.floorplan_capture_id,
          room.entry.floorplan_filename,
          room.entry.floorplan_media_type,
        );
      }
    };

    // ── Header row ────────────────────────────────────────────────────────
    const head = document.createElement("div");
    head.style.cssText = "display:flex;align-items:center;gap:6px;";

    const icon = document.createElement("span");
    icon.style.cssText = `font-size:12px;color:${isSelected ? "#4a9eff" : "var(--text-muted)"};flex-shrink:0;`;
    icon.textContent = "⬛";
    head.appendChild(icon);

    const lbl = document.createElement("span");
    lbl.style.cssText =
      `font-size:11px;flex:1;color:${isSelected ? "var(--text)" : "var(--text-muted)"};font-weight:${isSelected ? "600" : "400"};`;
    lbl.textContent = "Floor Plan";
    head.appendChild(lbl);

    const resetBtn = document.createElement("button");
    resetBtn.style.cssText = "background:none;border:none;cursor:pointer;padding:0 2px;line-height:1;display:flex;align-items:center;color:var(--text-muted);font-size:14px;";
    resetBtn.title = "Reset floor plan position";
    resetBtn.textContent = "↺";
    resetBtn.onclick = async (e) => {
      e.stopPropagation();
      this.opts.onResetFloorplan(index);
      await this.opts.onSave();
      this.refreshList();
    };
    head.appendChild(resetBtn);

    if (room.floorplanGroup) {
      const fpMirrored = room.entry.floorplan_mirror === true;
      const mirrorBtn = document.createElement("button");
      mirrorBtn.style.cssText =
        `background:none;border:none;cursor:pointer;padding:0 2px;font-size:13px;` +
        `opacity:${fpMirrored ? "1" : "0.4"};color:${fpMirrored ? "#4a9eff" : "var(--text-muted)"};` +
        `line-height:1;display:flex;align-items:center;`;
      mirrorBtn.title = fpMirrored ? "Disable mirror" : "Mirror floor plan";
      mirrorBtn.textContent = "⇔";
      mirrorBtn.onclick = (e) => {
        e.stopPropagation();
        this.opts.onToggleFloorplanMirror(index);
        this.refreshList();
      };
      head.appendChild(mirrorBtn);
    }

    const fpVisible = room.entry.floorplan_visible !== false;
    const eyeBtn = document.createElement("button");
    eyeBtn.className = "house-eye" + (room.floorplanGroup && fpVisible ? "" : " hidden");
    eyeBtn.textContent = "👁";
    eyeBtn.title = fpVisible ? "Hide floor plan" : "Show floor plan";
    eyeBtn.disabled = !room.floorplanGroup;
    eyeBtn.onclick = (e) => {
      e.stopPropagation();
      this.opts.composer.setRoomFloorplanVisible(index, !fpVisible);
      this.refreshList();
    };
    head.appendChild(eyeBtn);

    sub.appendChild(head);

    // ── Source list + opacity slider — only when selected ────────────────
    if (isSelected) {
      const listDiv = document.createElement("div");
      listDiv.style.cssText = "display:flex;flex-direction:column;gap:1px;padding:2px 0 0 18px;";
      listDiv.onclick = (e) => e.stopPropagation();

      for (const src of sources) {
        const isActive = room.entry.floorplan_capture_id === src.id;

        const rowEl = document.createElement("div");
        rowEl.style.cssText =
          "display:flex;align-items:center;gap:5px;cursor:pointer;padding:1px 0;";

        const typeTag = document.createElement("span");
        typeTag.style.cssText =
          "font-size:9px;color:var(--text-muted);background:var(--surface2,rgba(255,255,255,.06));" +
          "padding:1px 4px;border-radius:3px;flex-shrink:0;";
        typeTag.textContent = src.media_type === "roomplan_json" ? "JSON" : "SVG";

        const nameLbl = document.createElement("span");
        nameLbl.style.cssText =
          `font-size:10px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;` +
          `color:${isActive ? "var(--text)" : "var(--text-muted)"};font-weight:${isActive ? "600" : "400"};`;
        const shortName = (src.filename.split("/").pop() ?? src.filename).replace(/\.[^.]+$/, "");
        nameLbl.textContent = shortName;
        nameLbl.title = src.filename;

        rowEl.onclick = async () => {
          await this.opts.onSelectFloorplanSource(
            index, src.id, src.filename,
            src.media_type as "roomplan_json" | "floorplan_svg",
          );
        };

        rowEl.appendChild(typeTag);
        rowEl.appendChild(nameLbl);
        listDiv.appendChild(rowEl);
      }

      sub.appendChild(listDiv);

      if (room.floorplanGroup) {
        const sliderRow = document.createElement("div");
        sliderRow.style.cssText = "display:flex;align-items:center;gap:6px;padding:2px 0 2px 18px;";

        const slider = document.createElement("input");
        slider.type = "range";
        slider.min = "0";
        slider.max = "100";
        const currentOpacity = room.entry.floorplan_opacity ?? 0.7;
        slider.value = String(Math.round(currentOpacity * 100));
        slider.style.cssText = "flex:1;cursor:pointer;accent-color:#4a9eff;";

        const valEl = document.createElement("span");
        valEl.style.cssText = "font-size:11px;color:var(--text-muted);width:32px;text-align:right;flex-shrink:0;";
        valEl.textContent = `${slider.value}%`;

        slider.oninput = () => {
          const pct = parseInt(slider.value);
          valEl.textContent = `${pct}%`;
          this.opts.composer.setRoomFloorplanOpacity(index, pct / 100);
        };
        slider.onclick = (e) => e.stopPropagation();

        sliderRow.appendChild(slider);
        sliderRow.appendChild(valEl);
        sub.appendChild(sliderRow);
      }
    }

    return sub;
  }

  private _buildFloorplanRow(fp: FloorplanState, index: number): HTMLElement {
    const sel = this.opts.composer.getSelected();
    const isSelected = sel?.type === "floorplan" && sel.index === index;

    const row = document.createElement("div");
    row.setAttribute("data-fp-index", String(index));
    row.style.cssText = `
      padding:7px 10px;border-bottom:1px solid var(--border);cursor:pointer;
      background:${isSelected ? "var(--hover)" : ""};
      border-left:2px solid ${isSelected ? "var(--accent)" : "transparent"};
    `;
    row.onclick = () => {
      this.opts.composer.selectItem({ type: "floorplan", index });
      this.refreshList();
    };

    row.appendChild(this._buildRowHead(
      fp.entry.label,
      fp.entry.visible,
      fp.loading,
      fp.error,
      (visible) => { this.opts.composer.setFloorplanVisible(index, visible); row.style.opacity = visible ? "1" : "0.5"; },
      () => { this.opts.onRemoveItem("floorplan", index); this.refreshList(); },
    ));

    if (fp.loading) row.appendChild(this._buildProgressBar(`fp-${index}`, 0));

    if (isSelected && !fp.loading && !fp.error) {
      row.appendChild(this._buildXYZRotInputs(
        { x: fp.entry.x, y: fp.entry.y, z: fp.entry.z, rot: fp.entry.rotation },
        (x, y, z) => this.opts.composer.setFloorplanPosition(index, x, y, z),
        (deg) => this.opts.composer.setFloorplanRotationY(index, deg),
        `fp-${index}`,
      ));
    }

    row.style.opacity = fp.entry.visible ? "1" : "0.5";
    return row;
  }

  private _refreshActivePanoHighlight(): void {
    for (const el of this.listEl.querySelectorAll<HTMLElement>("[data-pano-index]")) {
      const idx = parseInt(el.dataset.panoIndex!);
      const sel = this.opts.composer.getSelected();
      const isSelected = sel?.type === "panorama" && sel.index === idx;
      const isActive = idx === this._activePanoIndex;
      el.style.background = isSelected || isActive ? "var(--hover)" : "";
      el.style.borderLeft = `2px solid ${isSelected ? "var(--accent)" : isActive ? "#4cc9f0" : "transparent"}`;
    }
  }

  private _buildPanoramaRow(pano: PanoramaState, index: number): HTMLElement {
    const sel = this.opts.composer.getSelected();
    const isSelected = sel?.type === "panorama" && sel.index === index;
    const isActive = index === this._activePanoIndex;

    const row = document.createElement("div");
    row.setAttribute("data-pano-index", String(index));
    row.style.cssText = `
      padding:7px 10px;border-bottom:1px solid var(--border);cursor:pointer;
      background:${isSelected || isActive ? "var(--hover)" : ""};
      border-left:2px solid ${isSelected ? "var(--accent)" : isActive ? "#4cc9f0" : "transparent"};
    `;
    row.onclick = () => {
      this.opts.composer.selectItem({ type: "panorama", index });
      this.refreshList();
    };

    const head = this._buildRowHead(
      `Panorama ${index + 1}`,
      pano.entry.visible,
      false,
      null,
      (visible) => { this.opts.composer.setPanoramaVisible(index, visible); row.style.opacity = visible ? "1" : "0.5"; },
      () => { this.opts.onRemoveItem("panorama", index); this.refreshList(); },
    );

    const previewBtn = document.createElement("button");
    previewBtn.textContent = "📷";
    previewBtn.title = "Toggle panorama photo preview";
    previewBtn.className = "house-preview-btn" + (this.opts.composer.hasPanoramaPreview(index) ? " active" : "");
    previewBtn.onclick = async (e) => {
      e.stopPropagation();
      if (this.opts.composer.hasPanoramaPreview(index)) {
        this.opts.composer.setPanoramaPreview(index, null);
        this._refreshPanoramaRow(index);
      } else {
        const url = await this.opts.onShowPanoramaPreview(index);
        if (url) {
          this.opts.composer.setPanoramaPreview(index, url);
          this._refreshPanoramaRow(index);
        }
      }
    };
    const enterBtn = document.createElement("button");
    enterBtn.textContent = "▶";
    enterBtn.title = "Enter panorama view";
    enterBtn.className = "house-preview-btn";
    enterBtn.onclick = (e) => {
      e.stopPropagation();
      void this.opts.composer.enterPanorama(index);
    };

    const eyeBtn = head.querySelector<HTMLButtonElement>(".house-eye");
    if (eyeBtn) {
      head.insertBefore(previewBtn, eyeBtn);
      head.insertBefore(enterBtn, eyeBtn);
    } else {
      head.appendChild(previewBtn);
      head.appendChild(enterBtn);
    }

    row.appendChild(head);

    if (pano.previewLoading) row.appendChild(this._buildProgressBar(`pano-${index}`, 0));

    if (isSelected) {
      row.appendChild(this._buildXYZRotInputs(
        { x: pano.entry.x, y: pano.entry.y, z: pano.entry.z, rot: pano.entry.yaw },
        (x, y, z) => this.opts.composer.setPanoramaPosition(index, x, y, z),
        (deg) => this.opts.composer.setPanoramaYaw(index, deg),
        `pano-${index}`,
        "Yaw°",
      ));
      if (this.opts.composer.hasPanoramaPreview(index)) {
        row.appendChild(this._buildPreviewControls(index));
      }
    }

    row.style.opacity = pano.entry.visible ? "1" : "0.5";
    return row;
  }

  private _buildPendingRow(captureId: string, label: string): HTMLElement {
    const row = document.createElement("div");
    row.setAttribute("data-pending-id", captureId);
    row.style.cssText =
      "padding:7px 10px;border-bottom:1px solid var(--border);opacity:0.6;";

    const head = document.createElement("div");
    head.style.cssText = "display:flex;align-items:center;gap:6px;";
    const dot = document.createElement("span");
    dot.style.cssText = "width:8px;height:8px;border-radius:50%;flex-shrink:0;background:#888;";
    head.appendChild(dot);
    const lbl = document.createElement("span");
    lbl.style.cssText =
      "font-size:12px;font-weight:600;flex:1;overflow:hidden;text-overflow:ellipsis;" +
      "white-space:nowrap;color:var(--text-muted);";
    lbl.textContent = label;
    head.appendChild(lbl);
    row.appendChild(head);

    const track = document.createElement("div");
    track.className = "hp-track";
    const inner = document.createElement("div");
    inner.className = "hp-indeterminate";
    track.appendChild(inner);
    row.appendChild(track);
    return row;
  }

  // ── Shared row building helpers ───────────────────────────────────────────

  private _buildRowHead(
    label: string,
    visible: boolean,
    loading: boolean,
    error: string | null,
    onToggleVisible: (v: boolean) => void,
    onRemove: () => void,
  ): HTMLElement {
    const head = document.createElement("div");
    head.style.cssText = "display:flex;align-items:center;gap:5px;";

    const dot = document.createElement("span");
    dot.style.cssText =
      "width:8px;height:8px;border-radius:50%;flex-shrink:0;background:" +
      (loading ? "#888" : error ? "var(--red)" : "var(--green,#4caf50)") + ";";
    head.appendChild(dot);

    const lbl = document.createElement("span");
    lbl.style.cssText =
      "font-size:12px;font-weight:600;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";
    lbl.textContent = label;
    head.appendChild(lbl);

    const eyeBtn = document.createElement("button");
    eyeBtn.className = "house-eye" + (visible ? "" : " hidden");
    eyeBtn.title = visible ? "Hide" : "Show";
    eyeBtn.textContent = "👁";
    eyeBtn.onclick = (e) => {
      e.stopPropagation();
      const newVisible = !visible;
      visible = newVisible;
      onToggleVisible(newVisible);
      eyeBtn.className = "house-eye" + (newVisible ? "" : " hidden");
      eyeBtn.title = newVisible ? "Hide" : "Show";
    };
    head.appendChild(eyeBtn);

    const removeBtn = document.createElement("button");
    removeBtn.textContent = "×";
    removeBtn.title = "Remove";
    removeBtn.style.cssText =
      "background:none;border:none;cursor:pointer;color:var(--text-muted);font-size:16px;line-height:1;padding:0 2px;";
    removeBtn.onclick = (e) => { e.stopPropagation(); onRemove(); };
    head.appendChild(removeBtn);

    return head;
  }

  private _buildProgressBar(key: string | number, pct: number): HTMLElement {
    const track = document.createElement("div");
    track.className = "hp-track";
    track.setAttribute("data-progress-track", String(key));
    const inner = document.createElement("div");
    inner.setAttribute("data-progress-fill", String(key));
    inner.className = pct > 0 ? "hp-fill" : "hp-indeterminate";
    if (pct > 0) inner.style.width = `${Math.round(pct * 100)}%`;
    track.appendChild(inner);
    return track;
  }

  private _buildRoomInputs(index: number): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-room-transform", String(index));
    wrap.style.cssText = "margin-top:6px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:4px;";

    const room = this.opts.composer.getRooms()[index];
    const pos = room.group.position;
    const rotY = (room.group.rotation.y * 180) / Math.PI;

    const fields: Array<{ label: string; value: number; onChange: (v: number) => void }> = [
      { label: "X", value: +pos.x.toFixed(3), onChange: (v) => this.opts.composer.setPosition(index, v, pos.y, pos.z) },
      { label: "Z", value: +pos.z.toFixed(3), onChange: (v) => this.opts.composer.setPosition(index, pos.x, pos.y, v) },
      { label: "Y°", value: +rotY.toFixed(1), onChange: (v) => this.opts.composer.setRotationY(index, v) },
    ];

    for (const f of fields) wrap.appendChild(this._numInput(f.label, f.value, f.label === "Y°" ? "5" : "0.1", f.onChange));
    return wrap;
  }

  private _buildXYZRotInputs(
    vals: { x: number; y: number; z: number; rot: number },
    onPos: (x: number, y: number, z: number) => void,
    onRot: (deg: number) => void,
    key: string,
    rotLabel = "Rot°",
  ): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-xyz-transform", key);
    wrap.style.cssText = "margin-top:6px;display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:4px;";

    const fields: Array<{ label: string; value: number; onChange: (v: number) => void }> = [
      { label: "X", value: +vals.x.toFixed(3), onChange: (v) => onPos(v, vals.y, vals.z) },
      { label: "Y", value: +vals.y.toFixed(3), onChange: (v) => onPos(vals.x, v, vals.z) },
      { label: "Z", value: +vals.z.toFixed(3), onChange: (v) => onPos(vals.x, vals.y, v) },
      { label: rotLabel, value: +vals.rot.toFixed(1), onChange: (v) => onRot(v) },
    ];

    for (const f of fields) wrap.appendChild(this._numInput(f.label, f.value, rotLabel === f.label ? "5" : "0.1", f.onChange));
    return wrap;
  }

  private _numInput(label: string, value: number, step: string, onChange: (v: number) => void): HTMLElement {
    const cell = document.createElement("div");
    cell.style.cssText = "display:flex;flex-direction:column;gap:2px;";

    const lbl = document.createElement("label");
    lbl.style.cssText = "font-size:10px;color:var(--text-muted);font-weight:600;";
    lbl.textContent = label;
    cell.appendChild(lbl);

    const inp = document.createElement("input");
    inp.type = "number";
    inp.step = step;
    inp.value = String(value);
    inp.style.cssText =
      "width:100%;background:var(--surface);border:1px solid var(--border);border-radius:3px;" +
      "color:var(--text);font-size:11px;padding:3px 5px;box-sizing:border-box;";
    inp.onchange = () => { const v = parseFloat(inp.value); if (!isNaN(v)) onChange(v); };
    cell.appendChild(inp);
    return cell;
  }

  // ── Row refresh helpers ───────────────────────────────────────────────────

  private _refreshRoomRow(index: number): void {
    const rooms = this.opts.composer.getRooms();
    const room = rooms[index];
    if (!room) { this.refreshList(); return; }
    const existing = this.listEl.querySelector<HTMLElement>(`[data-room-index="${index}"]`);
    if (existing) existing.replaceWith(this._buildRoomRow(room, index));
    else this.refreshList();
  }

  private _refreshFloorplanRow(index: number): void {
    const fps = this.opts.composer.getFloorplans();
    const fp = fps[index];
    if (!fp) { this.refreshList(); return; }
    const existing = this.listEl.querySelector<HTMLElement>(`[data-fp-index="${index}"]`);
    if (existing) existing.replaceWith(this._buildFloorplanRow(fp, index));
    else this.refreshList();
  }

  private _refreshPanoramaRow(index: number): void {
    const panos = this.opts.composer.getPanoramas();
    const pano = panos[index];
    if (!pano) { this.refreshList(); return; }
    const existing = this.listEl.querySelector<HTMLElement>(`[data-pano-index="${index}"]`);
    if (existing) existing.replaceWith(this._buildPanoramaRow(pano, index));
    else this.refreshList();
  }

  private _buildPreviewControls(index: number): HTMLElement {
    const p = this.opts.composer.getPanoramas()[index];
    const currentOpacity = p?.preview
      ? (p.preview.material as unknown as { opacity: number }).opacity
      : 0.6;
    const pct = Math.round(currentOpacity * 100);
    const mirrored = p?.entry.mirror ?? false;

    const wrap = document.createElement("div");
    wrap.style.cssText = "margin-top:6px;display:flex;flex-direction:column;gap:5px;";

    // Opacity row
    const opRow = document.createElement("div");
    opRow.style.cssText = "display:flex;align-items:center;gap:6px;";

    const lbl = document.createElement("label");
    lbl.style.cssText = "font-size:10px;color:var(--text-muted);font-weight:600;white-space:nowrap;";
    lbl.textContent = "Opacity";
    opRow.appendChild(lbl);

    const slider = document.createElement("input");
    slider.type = "range";
    slider.min = "0";
    slider.max = "100";
    slider.value = String(pct);
    slider.style.cssText = "flex:1;cursor:pointer;accent-color:var(--accent,#5b8dd9);";
    opRow.appendChild(slider);

    const val = document.createElement("span");
    val.style.cssText = "font-size:10px;color:var(--text-muted);width:30px;text-align:right;";
    val.textContent = `${pct}%`;
    opRow.appendChild(val);

    slider.oninput = () => {
      const v = parseInt(slider.value);
      val.textContent = `${v}%`;
      this.opts.composer.setPanoramaPreviewOpacity(index, v / 100);
    };

    wrap.appendChild(opRow);

    // Mirror button
    const mirrorBtn = document.createElement("button");
    mirrorBtn.className = "btn";
    mirrorBtn.textContent = "⇔ Mirror";
    mirrorBtn.style.cssText = `font-size:11px;padding:3px 8px;align-self:flex-start;
      ${mirrored ? "border-color:var(--accent);color:var(--accent);" : ""}`;
    mirrorBtn.onclick = (e) => {
      e.stopPropagation();
      this.opts.composer.togglePanoramaPreviewMirror(index);
    };
    wrap.appendChild(mirrorBtn);

    return wrap;
  }

  private _refreshTransformInputs(sel: SelectedItem): void {
    if (sel.type === "walls" || sel.type === "roomFloorplan") {
      // Transform-only selections: no numeric inputs, gizmo handles move/rotate.
      return;
    }
    if (sel.type === "room") {
      const existing = this.listEl.querySelector<HTMLElement>(`[data-room-transform="${sel.index}"]`);
      if (existing) existing.replaceWith(this._buildRoomInputs(sel.index));
    } else if (sel.type === "floorplan") {
      const fp = this.opts.composer.getFloorplans()[sel.index];
      if (!fp) return;
      const existing = this.listEl.querySelector<HTMLElement>(`[data-xyz-transform="fp-${sel.index}"]`);
      if (existing) existing.replaceWith(this._buildXYZRotInputs(
        { x: fp.entry.x, y: fp.entry.y, z: fp.entry.z, rot: fp.entry.rotation },
        (x, y, z) => this.opts.composer.setFloorplanPosition(sel.index, x, y, z),
        (deg) => this.opts.composer.setFloorplanRotationY(sel.index, deg),
        `fp-${sel.index}`,
      ));
    } else if (sel.type === "panorama") {
      const p = this.opts.composer.getPanoramas()[sel.index];
      if (!p) return;
      const existing = this.listEl.querySelector<HTMLElement>(`[data-xyz-transform="pano-${sel.index}"]`);
      if (existing) existing.replaceWith(this._buildXYZRotInputs(
        { x: p.entry.x, y: p.entry.y, z: p.entry.z, rot: p.entry.yaw },
        (x, y, z) => this.opts.composer.setPanoramaPosition(sel.index, x, y, z),
        (deg) => this.opts.composer.setPanoramaYaw(sel.index, deg),
        `pano-${sel.index}`,
        "Yaw°",
      ));
    }
  }

  private _updateProgress(index: number, pct: number): void {
    const fill = this.listEl.querySelector<HTMLElement>(`[data-progress-fill="${index}"]`);
    if (!fill) return;
    if (fill.classList.contains("hp-indeterminate")) {
      fill.classList.replace("hp-indeterminate", "hp-fill");
    }
    fill.style.width = `${Math.round(pct * 100)}%`;
  }

  // ── Footer actions ────────────────────────────────────────────────────────

  private async _doGenerateClientLink(): Promise<void> {
    this.clientLinkBtn.disabled = true;
    this.clientLinkBtn.textContent = "Generating…";
    try {
      const { url, expires_in_days } = await this.opts.onGenerateClientLink();
      this.clientLinkBtn.textContent = "Link copied ✓";
      await navigator.clipboard.writeText(url).catch(() => {
        window.prompt(`Client link (valid ${expires_in_days} days):`, url);
      });
      setTimeout(() => {
        this.clientLinkBtn.textContent = "Generate client link";
        this.clientLinkBtn.disabled = false;
      }, 2000);
    } catch {
      this.clientLinkBtn.textContent = "Generate client link";
      this.clientLinkBtn.disabled = false;
    }
  }

  private async _doSave(): Promise<void> {
    this.saveBtn.disabled = true;
    this.saveBtn.textContent = "Saving…";
    try {
      await this.opts.onSave();
      this.saveBtn.textContent = "Saved ✓";
      setTimeout(() => { this.saveBtn.textContent = "Save"; this.saveBtn.disabled = false; }, 1500);
    } catch {
      this.saveBtn.textContent = "Save";
      this.saveBtn.disabled = false;
    }
  }

  private async _doExport(btn: HTMLButtonElement): Promise<void> {
    btn.disabled = true;
    btn.textContent = "Exporting…";
    try {
      await this.opts.onExport();
    } finally {
      btn.textContent = "Export GLB";
      btn.disabled = false;
    }
  }
}
